import {
  ANOMALY_COLORS,
  ANOMALY_CRITICAL_THRESHOLD,
  ANOMALY_WARNING_THRESHOLD,
  DAY_IN_MS,
  TIMELINE_AXIS_OFFSET_Y,
  TIMELINE_BAR_MIN_HEIGHT,
  TIMELINE_BAR_Y,
  TIMELINE_BOTTOM_PADDING,
  TIMELINE_LABEL_OFFSET_Y,
  TIMELINE_PADDING_X,
  TIMELINE_STRIPE_WIDTH,
  TIMELINE_TICKS_COUNT,
  TIMELINE_TOOLTIP_OFFSET,
  TIMELINE_TOOLTIP_VIEWPORT_PADDING,
  TIMELINE_WIDTH,
} from "./constants";
import {
  IAnomalyData,
  IAnomalyItem,
  IAnomalyStripe,
  IAnomalyTimelineModel,
  IShortTermPredictItem,
  TAnomalyLevel,
  TTooltipPosition,
} from "./interfaces";
import { getAnomalyWeightColor } from "../../../forecasting/utils";

const HTML_LINE_BREAK_PATTERN = /<br\s*\/?>/gi;

export const formatShortTermPredictText = (text: string): string => text.replace(HTML_LINE_BREAK_PATTERN, "\n");

export const getShortTermPredictBuildDate = (item: IShortTermPredictItem): string =>
  item.forecastIntervalStart || item.date;

const HOUR_IN_MS = 60 * 60 * 1000;

const DEFAULT_HORIZON_HOURS_BY_MODEL_TYPE: Partial<Record<number, number>> = {
  1: 72,
  2: 336,
};

const resolveHorizonHours = (item: IShortTermPredictItem): number | null => {
  if (typeof item.horizonHours === "number" && Number.isFinite(item.horizonHours)) {
    return item.horizonHours;
  }

  const modelType = item.modelType ?? item.predictType;
  return DEFAULT_HORIZON_HOURS_BY_MODEL_TYPE[modelType] ?? null;
};

export const getShortTermPredictValidUntilDate = (item: IShortTermPredictItem): string | null => {
  if (item.forecastIntervalEnd) {
    const endTimestamp = new Date(item.forecastIntervalEnd).getTime();
    return Number.isFinite(endTimestamp) ? item.forecastIntervalEnd : null;
  }

  const buildDate = getShortTermPredictBuildDate(item);
  const buildTimestamp = new Date(buildDate).getTime();
  const horizonHours = resolveHorizonHours(item);

  if (!Number.isFinite(buildTimestamp) || horizonHours === null) {
    return null;
  }

  return new Date(buildTimestamp + horizonHours * HOUR_IN_MS).toISOString();
};

const getUtcDateLabel = (timestamp: number, includeTime = false): string => {
  const date = new Date(timestamp);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");

  if (!includeTime) {
    return `${year}-${month}-${day}`;
  }

  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${month}-${day} ${hours}:${minutes}`;
};

export const createEmptyAnomalyData = (): IAnomalyData => ({
  items: [],
  count: 0,
  l1: 0,
  l2: 0,
  l3: 0,
});

export const normalizeAnomalyData = (json: IAnomalyData | null | undefined): IAnomalyData => ({
  items: Array.isArray(json?.items)
    ? [...json.items]
        .map(item => ({
          Date: item?.Date ?? "",
          Value: typeof item?.Value === "number" ? item.Value : 0,
        }))
        .filter(item => Number.isFinite(new Date(item.Date).getTime()))
        .sort((left, right) => new Date(left.Date).getTime() - new Date(right.Date).getTime())
    : [],
  count: typeof json?.count === "number" ? json.count : 0,
  l1: typeof json?.l1 === "number" ? json.l1 : 0,
  l2: typeof json?.l2 === "number" ? json.l2 : 0,
  l3: typeof json?.l3 === "number" ? json.l3 : 0,
});

export const getAnomalyLevel = (value: number): TAnomalyLevel => {
  if (value >= ANOMALY_CRITICAL_THRESHOLD) {
    return "l3";
  }

  if (value > ANOMALY_WARNING_THRESHOLD && value < ANOMALY_CRITICAL_THRESHOLD) {
    return "l2";
  }

  return "l1";
};

export const getAnomalyStripeColor = (value: number): string => ANOMALY_COLORS[getAnomalyLevel(value)];

export const buildAnomalyTimelineModel = (
  items: IAnomalyItem[],
  startTimestamp: string | Date,
  endTimestamp: string | Date,
): IAnomalyTimelineModel => {
  const fallbackStart = items.length ? new Date(items[0].Date).getTime() : Date.now();
  const fallbackEnd = items.length ? new Date(items[items.length - 1].Date).getTime() : fallbackStart + DAY_IN_MS;
  const parsedStart = new Date(startTimestamp).getTime();
  const parsedEnd = new Date(endTimestamp).getTime();

  const start = Number.isFinite(parsedStart) ? parsedStart : fallbackStart;
  const endBase = Number.isFinite(parsedEnd) ? parsedEnd : fallbackEnd;
  const end = endBase > start ? endBase : start + 1;
  const duration = end - start;
  const barStart = TIMELINE_PADDING_X;
  const barEnd = TIMELINE_WIDTH - TIMELINE_PADDING_X;
  const barWidth = barEnd - barStart;
  const includeTime = duration <= 2 * DAY_IN_MS;

  const toPosition = (timestamp: number): number => {
    const clamped = Math.min(Math.max(timestamp, start), end);
    return barStart + ((clamped - start) / duration) * barWidth;
  };

  const toStripePosition = (timestamp: number): number => {
    const centeredPosition = toPosition(timestamp) - TIMELINE_STRIPE_WIDTH / 2;
    return Math.min(Math.max(centeredPosition, barStart), barEnd - TIMELINE_STRIPE_WIDTH);
  };

  const positionedItems = items
    .map((item, index) => {
      const timestamp = new Date(item.Date).getTime();

      if (!Number.isFinite(timestamp)) {
        return null;
      }

      return {
        key: `${item.Date}-${index}`,
        ...item,
        color: getAnomalyWeightColor(item.Value),
        level: getAnomalyLevel(item.Value),
        x: toStripePosition(timestamp),
      };
    })
    .filter((item): item is Omit<IAnomalyStripe, "y" | "height"> => item !== null)
    .sort((left, right) => left.x - right.x);

  const barHeight = TIMELINE_BAR_MIN_HEIGHT;
  const stripes = positionedItems.map(item => ({
    ...item,
    y: TIMELINE_BAR_Y,
    height: barHeight,
  })) satisfies IAnomalyStripe[];

  const axisY = TIMELINE_BAR_Y + barHeight + TIMELINE_AXIS_OFFSET_Y;
  const labelY = axisY + TIMELINE_LABEL_OFFSET_Y;
  const height = labelY + TIMELINE_BOTTOM_PADDING;
  const ticks = Array.from({ length: TIMELINE_TICKS_COUNT + 1 }, (_, index) => {
    const ratio = index / TIMELINE_TICKS_COUNT;
    const timestamp = start + duration * ratio;

    return {
      key: `tick-${index}`,
      label: getUtcDateLabel(timestamp, includeTime),
      x: barStart + barWidth * ratio,
    };
  });

  return {
    barStart,
    barWidth,
    barHeight,
    height,
    axisY,
    labelY,
    stripes,
    ticks,
  };
};

interface ISize {
  width: number;
  height: number;
}

interface IRect extends ISize {
  left: number;
  top: number;
}

interface ICalculateAnomalyTooltipPositionParams {
  activeStripe: IAnomalyStripe;
  svgRect: IRect;
  tooltipSize: ISize;
  timelineHeight: number;
  viewportSize: ISize;
}

const clampTooltipCoordinate = (coordinate: number, tooltipSize: number, viewportSize: number): number => {
  const maxCoordinate = Math.max(
    viewportSize - tooltipSize - TIMELINE_TOOLTIP_VIEWPORT_PADDING,
    TIMELINE_TOOLTIP_VIEWPORT_PADDING,
  );

  return Math.min(Math.max(coordinate, TIMELINE_TOOLTIP_VIEWPORT_PADDING), maxCoordinate);
};

export const calculateAnomalyTooltipPosition = ({
  activeStripe,
  svgRect,
  tooltipSize,
  timelineHeight,
  viewportSize,
}: ICalculateAnomalyTooltipPositionParams): TTooltipPosition => {
  const scaleX = svgRect.width / TIMELINE_WIDTH;
  const scaleY = svgRect.height / timelineHeight;
  const anchorX = svgRect.left + (activeStripe.x + TIMELINE_STRIPE_WIDTH / 2) * scaleX;
  const anchorY = svgRect.top + (activeStripe.y + activeStripe.height / 2) * scaleY;
  const topBelowMarker = anchorY + TIMELINE_TOOLTIP_OFFSET;
  const preferredTop =
    topBelowMarker + tooltipSize.height <= viewportSize.height - TIMELINE_TOOLTIP_VIEWPORT_PADDING
      ? topBelowMarker
      : anchorY - tooltipSize.height - TIMELINE_TOOLTIP_OFFSET;

  return {
    left: clampTooltipCoordinate(anchorX - tooltipSize.width / 2, tooltipSize.width, viewportSize.width),
    top: clampTooltipCoordinate(preferredTop, tooltipSize.height, viewportSize.height),
  };
};
